import { useCallback, useEffect, useState } from "react";

import { api, clinicHeader, readError } from "../api/client";
import { ErrorNote } from "./ErrorNote";

type Shot = {
  id: string;
  payload: {
    vaccine_text?: string;
    batch_number?: string;
    site?: string;
    route?: string;
    dose_number?: number | null;
    occurrence_datetime?: string;
    expiry_date?: string | null;
  };
};

const SITES = [
  ["left_arm", "Left arm"],
  ["right_arm", "Right arm"],
  ["left_thigh", "Left thigh"],
  ["right_thigh", "Right thigh"],
  ["oral", "By mouth"],
  ["other", "Other"],
] as const;

const ROUTES = [
  ["intramuscular", "Intramuscular"],
  ["subcutaneous", "Subcutaneous"],
  ["intradermal", "Intradermal"],
  ["oral", "Oral"],
  ["nasal", "Nasal"],
] as const;

const SITE_LABELS = Object.fromEntries(SITES);

function when(iso?: string) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-MY", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * What this clinic gave.
 *
 * It says nothing about what the patient is due. Healtrics ships no
 * immunisation schedule, because a schedule is clinical guidance and needs a
 * clinical owner (CLN-F-137). A screen that showed one would be making a
 * clinical statement with nobody's name against it.
 *
 * The batch number is the reason this is a panel rather than a note. A
 * manufacturer recalls a lot and the clinic needs to know whose arm it went
 * into, so the field is required and it is shown on every row.
 *
 * A nurse records one, because a nurse gives one.
 */
export function ImmunisationPanel({
  patientId,
  canRecord,
}: {
  patientId: string;
  canRecord: boolean;
}) {
  const [rows, setRows] = useState<Shot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    const { data, error: failed } = await api.GET("/portal/patients/{id}/immunisations", {
      params: { path: { id: patientId }, header: clinicHeader() },
    });
    setLoading(false);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setError(null);
    setRows((data?.items ?? []) as Shot[]);
  }, [patientId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return null;

  return (
    <section className="shots">
      <div className="shots-head">
        <h2>Vaccinations</h2>
        {canRecord && !adding && (
          <button type="button" onClick={() => setAdding(true)}>
            Record one
          </button>
        )}
      </div>

      <ErrorNote message={error} />

      {adding && (
        <ShotForm
          patientId={patientId}
          onDone={() => {
            setAdding(false);
            void load();
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      {rows.length === 0 && !adding && (
        <p className="empty">No vaccinations on record at this clinic.</p>
      )}

      {rows.map((row) => (
        <div className="shot" key={row.id}>
          <span className="grow">
            <strong>{row.payload?.vaccine_text}</strong>
            <span className="muted">
              {" · "}
              {when(row.payload?.occurrence_datetime)}
              {row.payload?.site && ` · ${SITE_LABELS[row.payload.site] ?? row.payload.site}`}
              {row.payload?.dose_number ? ` · dose ${row.payload.dose_number}` : ""}
            </span>
          </span>
          {/* Shown on every row, because a recall starts by reading it. */}
          <span className="shot-batch mono">{row.payload?.batch_number}</span>
        </div>
      ))}
    </section>
  );
}

function ShotForm({
  patientId,
  onDone,
  onCancel,
}: {
  patientId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [vaccine, setVaccine] = useState("");
  const [batch, setBatch] = useState("");
  const [site, setSite] = useState<string>("left_arm");
  const [route, setRoute] = useState<string>("intramuscular");
  const [dose, setDose] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    const { error: failed } = await api.POST("/portal/patients/{id}/immunisations", {
      params: { path: { id: patientId }, header: clinicHeader() },
      body: {
        vaccine_text: vaccine.trim(),
        batch_number: batch.trim(),
        site: site as (typeof SITES)[number][0],
        route: route as (typeof ROUTES)[number][0],
        dose_number: dose ? Number(dose) : null,
      },
    });
    setSaving(false);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    onDone();
  };

  return (
    <form className="record-form" onSubmit={submit}>
      <label>
        Vaccine
        <input
          value={vaccine}
          onChange={(e) => setVaccine(e.target.value)}
          placeholder="Influenza vaccine"
          required
          autoFocus
        />
      </label>

      <label>
        Batch number
        <input
          value={batch}
          onChange={(e) => setBatch(e.target.value)}
          placeholder="Read it off the vial"
          required
        />
      </label>
      {/* The one field with no workaround, so the form says why. */}
      <p className="hint">
        Required. A recall asks which arm a batch went into, and nothing else answers it.
      </p>

      <label>
        Site
        <select value={site} onChange={(e) => setSite(e.target.value)}>
          {SITES.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>

      <label>
        Route
        <select value={route} onChange={(e) => setRoute(e.target.value)}>
          {ROUTES.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>

      <label>
        Dose number <span className="optional">optional</span>
        <input type="number" min="1" value={dose} onChange={(e) => setDose(e.target.value)} />
      </label>

      <ErrorNote message={error} />

      <div className="row-actions">
        <button type="submit" disabled={saving || !vaccine.trim() || !batch.trim()}>
          {saving ? "Recording…" : "Record"}
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
